import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { securePut } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';

const SubscriptionForm = ({
  open,
  onClose,
  client,
  subscriptionData,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const subscriptionPlanOptions = useMemo(() => [
    { value: 'ESSENTIAL', label: t('plans.ESSENTIAL') },
    { value: 'GROWTH', label: t('plans.GROWTH') },
    { value: 'ENTERPRISE', label: t('plans.ENTERPRISE') },
  ], [t]);
  const billingIntervalOptions = useMemo(() => [
    { value: 'MONTHLY', label: t('billingIntervals.MONTHLY') },
    { value: 'QUARTERLY', label: t('billingIntervals.QUARTERLY') },
    { value: 'YEARLY', label: t('billingIntervals.YEARLY') },
  ], [t]);
  const hostingModeOptions = useMemo(() => [
    { value: 'SAAS', label: t('fields.clients.subscription.includedHostingMode.SAAS') },
    { value: 'ON_PREMISE', label: t('fields.clients.subscription.includedHostingMode.ON_PREMISE') },
  ], [t]);

  const subscriptionFormFields = useMemo(() => [
    { name: 'planName', label: t('fields.clients.subscription.planName'), type: 'dropdown', required: true, options: subscriptionPlanOptions },
    { name: 'billingInterval', label: t('fields.clients.subscription.billingIntervalLabel'), type: 'dropdown', required: true, options: billingIntervalOptions },
    { name: 'currentPeriodStart', label: t('fields.clients.subscription.currentPeriodStart'), type: 'date' , required: true},
    { name: 'discount', label: t('fields.clients.subscription.discount'), type: 'text' },
    { name: 'currency', label: t('fields.clients.subscription.currency'), type: 'text' },
    { name: 'tva', label: t('fields.clients.subscription.tva'), type: 'text' },
    { name: 'includedSeats', label: t('fields.clients.subscription.includedSeats'), type: 'text' , required: true},
    { name: 'pricePerUser', label: t('fields.clients.subscription.pricePerUser'), type: 'text' , required: true},
  ], [t, subscriptionPlanOptions, billingIntervalOptions, hostingModeOptions]);

  const hostingFormFieldsBase = useMemo(() => [
    { name: 'currentHostingMode', label: t('fields.clients.hosting.currentHostingMode'), type: 'dropdown', options: hostingModeOptions, required: true },
    { name: 'environmentId', label: t('fields.clients.hosting.environmentId'), type: 'text' },
    { name: 'dbName', label: t('fields.clients.hosting.dbName'), type: 'text' },
    { name: 'baseDomain', label: t('fields.clients.hosting.baseDomain'), type: 'text' },
  ], [t, hostingModeOptions]);

  const isSaas = formValues.currentHostingMode === 'SAAS';
  const hostingFormFields = useMemo(() => {
    if (isSaas) {
      return hostingFormFieldsBase.filter((f) => f.name !== 'dbName');
    }
    return hostingFormFieldsBase;
  }, [hostingFormFieldsBase, isSaas]);

  useEffect(() => {
    if (!open || !client?.id) return;
    const sub = subscriptionData?.subscription != null
      ? (Array.isArray(subscriptionData.subscription) ? subscriptionData.subscription[0] : subscriptionData.subscription)
      : (Array.isArray(subscriptionData) ? subscriptionData?.[0] : subscriptionData);
    const plan = sub?.subscriptionPlanName;
    const isEssential = plan === 'ESSENTIAL';
    const isGrowthOrEnterprise = plan === 'GROWTH' || plan === 'ENTERPRISE';
    const derivedHosting = isEssential ? 'SAAS' : (isGrowthOrEnterprise ? 'ON_PREMISE' : '');
    const derivedPrice = plan === 'ESSENTIAL' ? 150 : plan === 'GROWTH' ? 250 : (sub?.pricePerUser ?? sub?.subscriptionPricePerUser ?? '');
    setFormValues({
      id: sub?.id ?? '',
      planName: plan,
      billingInterval: sub?.subscriptionBillingInterval,
      discount: sub?.discount ?? sub?.subscriptionDiscount ?? '',
      currency: sub?.currency ?? sub?.subscriptionCurrency ?? '',
      tva: sub?.tva ?? sub?.subscriptionTVA ?? '',
      currentPeriodStart: sub?.currentPeriodStart ?? sub?.subscriptionCurrentPeriodStart ?? '',
      currentPeriodEnd: sub?.currentPeriodEnd ?? sub?.subscriptionCurrentPeriodEnd ?? '',
      nextBillingAt: sub?.nextBillingAt ?? sub?.subscriptionNextBillingAt ?? '',
      includedHostingMode: isEssential ? 'SAAS' : (isGrowthOrEnterprise ? 'ON_PREMISE' : (sub?.includedHostingMode ?? 'ON_PREMISE')),
      includedSeats: sub?.includedSeats ?? sub?.subscriptionIncludedSeats ?? '',
      extraSeats: sub?.extraSeats ?? sub?.subscriptionExtraSeats ?? '',
      includedOrdersPerMonth: sub?.includedOrdersPerMonth ?? sub?.subscriptionIncludedOrdersPerMonth ?? '',
      includedStorageGb: sub?.includedStorageGb ?? sub?.subscriptionIncludedStorageGb ?? '',
      pricePerUser: derivedPrice,
      currentHostingMode: isEssential ? 'SAAS' : (isGrowthOrEnterprise ? 'ON_PREMISE' : (sub?.hostingMode ?? derivedHosting)),
      environmentId: sub?.hostingEnvironmentId,
      dbName: sub?.hostingDbName ?? '',
      baseDomain: sub?.hostingBaseDomain ?? '',
    });
  }, [open, client?.id, client?.hosting, subscriptionData]);

  const handleChange = (name, value) => {
    setFormValues(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'planName') {
        if (value === 'ESSENTIAL') {
          next.pricePerUser = 150;
          next.currentHostingMode = 'SAAS';
          next.includedHostingMode = 'SAAS';
        } else if (value === 'GROWTH') {
          next.pricePerUser = 250;
          next.currentHostingMode = 'ON_PREMISE';
          next.includedHostingMode = 'ON_PREMISE';
        } else if (value === 'ENTERPRISE') {
          next.pricePerUser = '';
          next.currentHostingMode = 'ON_PREMISE';
          next.includedHostingMode = 'ON_PREMISE';
        }
      }
      return next;
    });
  };

  const toISODateString = (v) => {
    if (v == null || v === '') return null;
    const dateStr = typeof v === 'string' ? v : v?.date ?? v?.date?.date;
    if (!dateStr) return null;
    if (dateStr.includes('T')) return dateStr;
    return `${dateStr}T00:00:00.000+01:00`;
  };

  const num = (v) => (v != null && v !== '' ? Number(v) : undefined);
  const str = (v) => (v != null && v !== '' ? String(v) : undefined);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const grayClientId = client?.id ;
      const payload = {
        grayClientId: grayClientId != null ? Number(grayClientId) : undefined,
        subscriptionPlanName: str(formValues.planName),
        subscriptionBillingInterval: str(formValues.billingInterval),
        subscriptionDiscount: num(formValues.discount),
        subscriptionCurrency: str(formValues.currency),
        subscriptionTVA: num(formValues.tva),
        subscriptionCurrentPeriodStart: toISODateString(formValues.currentPeriodStart),
        subscriptionCurrentPeriodEnd: toISODateString(formValues.currentPeriodEnd),
        subscriptionNextBillingAt: toISODateString(formValues.nextBillingAt),
        subscriptionIncludedHostingMode: str(formValues.includedHostingMode),
        subscriptionIncludedSeats: num(formValues.includedSeats),
        subscriptionExtraSeats: num(formValues.extraSeats),
        subscriptionIncludedOrdersPerMonth: num(formValues.includedOrdersPerMonth),
        subscriptionIncludedStorageGb: num(formValues.includedStorageGb),
        subscriptionPricePerUser: num(formValues.pricePerUser),
        hostingMode: str(formValues.currentHostingMode),
        hostingEnvironmentId: str(formValues.environmentId),
        hostingDbName: str(formValues.dbName),
        hostingStorageBucket: str(formValues.storageBucket),
        hostingBaseDomain: str(formValues.baseDomain),
      };
      if (formValues.id != null && formValues.id !== '') {
        payload.id = Number(formValues.id);
      }
      const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([, v]) => v !== undefined)
      );
      await securePut('/subscriptions', cleanPayload, showSnackbar);
      showSnackbar(t('confirmation.clientsEdit') || 'Subscription updated', 'success');
      onSuccess?.();
    } catch (err) {
      console.error(err);
      showSnackbar(t('errors.generic'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
        <div className="bg-[#F9FAFC] rounded-xl px-6 py-8 w-11/12 max-w-5xl max-h-[90vh]  overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#38485C] mb-4">
            {t('fields.clients.subscriptionTitle')} &amp; {t('fields.clients.hostingTitle')}
          </h3>
          <div className="space-y-8">
            <div>
              <h4 className="text-[16px] font-medium text-[#ACAFC6] mb-2">{t('fields.clients.subscriptionTitle')}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {subscriptionFormFields.map((field) => (
                  <Input
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    type={field.type || 'text'}
                    value={formValues[field.name] ?? ''}
                    onChange={(value) => handleChange(field.name, value)}
                    options={field.options}
                    required={field.required}
                  />
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-[16px] font-medium text-[#ACAFC6] mb-2">{t('fields.clients.hostingTitle')}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {hostingFormFields.map((field) => (
                  <Input
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    type={field.type || 'text'}
                    value={formValues[field.name] ?? ''}
                    onChange={(value) => handleChange(field.name, value)}
                    options={field.options}
                    required={field.required}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6">
            <SaveCancel
              onSaveClick={() => !saving && handleSubmit()}
              onCancelClick={onClose}
            />
          </div>
        </div>
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default SubscriptionForm;
